import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Footer, Navbar } from "./Home";

/* ─── Image map ─── */
const IMG = {
  warehouse: "/assets/images/new/2.jpg",
  whoWeAre: "/assets/images/new/8.jpg",
  visionMission: "/assets/images/new/3.jpg",
  freshProduce: "/assets/images/new/4.jpg",
  supplyChain: "/assets/images/new/5.jpg",
  globalNetwork: "/assets/images/new/6.jpg",
  customers: "/assets/images/new/7.jpg",
  strength: "/assets/images/new/8.jpg",
  contact: "/assets/images/new/9.jpg",
  pineappleDole: "/assets/images/new/10.jpg",
  bananaDole: "/assets/images/new/11.jpg",
  pineappleOla: "/assets/images/new/12.jpg",
  pineappleOla2: "/assets/images/new/13.jpg",
  bananaTerioka: "/assets/images/new/15.png",
  whyPartner: "/assets/images/new/13.jpg",
};

/* ─── Utilities ─── */
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

function useIsMobile(bp = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= bp : false
  );
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= bp);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, [bp]);
  return isMobile;
}


function useScrollY() {
  const [y, setY] = useState(0);
  useEffect(() => {
    const fn = () => setY(window.scrollY);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return y;
}

function useInView(ref, threshold = 0.1) {
  const [vis, setVis] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const o = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setVis(true);
      },
      { threshold },
    );
    o.observe(ref.current);
    return () => o.disconnect();
  }, []);
  return vis;
}

function useStickyProgress(ref) {
  const [prog, setProg] = useState(0);
  useEffect(() => {
    const fn = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const total = ref.current.offsetHeight - window.innerHeight;
      setProg(clamp(-rect.top / total, 0, 1));
    };
    window.addEventListener("scroll", fn, { passive: true });
    fn();
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return prog;
}

/* ─── Global CSS ─── */
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,800;0,900;1,700&family=DM+Sans:wght@300;400;500;600&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'DM Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;overflow-x:hidden;background:#fff}
  @keyframes fadeUp{from{opacity:0;transform:translateY(36px)}to{opacity:1;transform:translateY(0)}}
  @keyframes shimmer{0%{background-position:200% center}100%{background-position:-200% center}}
  @keyframes scrollDot{0%{transform:translateY(-100%)}100%{transform:translateY(250%)}}
  @keyframes pulseGreen{0%,100%{box-shadow:0 0 0 0 rgba(221,60,48,0.4)}50%{box-shadow:0 0 0 14px rgba(221,60,48,0)}}
  @keyframes floatY{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}
  @keyframes orbGrow{0%,100%{transform:scale(1);opacity:0.5}50%{transform:scale(1.2);opacity:0.9}}
  @keyframes orbPulse{0%,100%{transform:scale(1);opacity:0.6}50%{transform:scale(1.15);opacity:1}}
  @keyframes fadeSlideUp{0%{opacity:0;transform:translateY(20px)}100%{opacity:1;transform:translateY(0)}}
  @keyframes scaleIn{from{opacity:0;transform:scale(0.8)}to{opacity:1;transform:scale(1)}}
  @keyframes ripple{0%{transform:scale(0);opacity:1}100%{transform:scale(4);opacity:0}}
  @keyframes fadeIn{from{opacity:0}to{opacity:1}}
  .ani-fadeup{animation:fadeUp 0.85s cubic-bezier(.22,1,.36,1) both}
  .pf{font-family:'Playfair Display',Georgia,serif}
  .dm{font-family:'DM Sans',sans-serif}
  .shine-text{
    background:linear-gradient(90deg,#dd3c30 0%,#fff 40%,#dd3c30 60%,#fff 100%);
    background-size:300% 100%;-webkit-background-clip:text;-webkit-text-fill-color:transparent;
    background-clip:text;animation:shimmer 4s linear infinite
  }
  .shimmer-green{
    background:linear-gradient(90deg,#276530 0%,#dd3c30 30%,#276530 60%,#dd3c30 90%,#276530 100%);
    background-size:300% 100%;-webkit-background-clip:text;-webkit-text-fill-color:transparent;
    background-clip:text;animation:shimmer 5s linear infinite
  }
  .progress-pill{width:8px;height:8px;border-radius:4px;background:rgba(255,255,255,0.25);transition:all 0.4s ease}
  .progress-pill.active{width:28px;background:#dd3c30}
  .sl{font-size:11px;letter-spacing:3px;color:#dd3c30;font-weight:700;text-transform:uppercase;margin-bottom:14px;font-family:'DM Sans',sans-serif}
  .sl-dark{font-size:11px;letter-spacing:3px;color:#276530;font-weight:700;text-transform:uppercase;margin-bottom:14px;font-family:'DM Sans',sans-serif}
  .btn-green{display:inline-block;background:#276530;color:#fff;padding:13px 50px;border-radius:10px;text-decoration:none;font-weight:500;font-size:15px;transition:transform 0.2s,box-shadow 0.2s;border:none;cursor:pointer;box-shadow:0 6px 20px rgba(39,101,48,0.35)}
  .btn-green:hover{transform:scale(1.04);box-shadow:0 10px 28px rgba(39,101,48,0.45)}
  .btn-ghost{display:inline-block;background:rgba(255,255,255,0.12);color:#fff;padding:13px 40px;border-radius:10px;text-decoration:none;font-weight:500;font-size:15px;border:1.5px solid rgba(255,255,255,0.3);transition:background 0.2s;cursor:pointer}
  .btn-ghost:hover{background:rgba(255,255,255,0.22)}
  .nav-secondary{position:sticky;top:56px;z-index:900;background:rgba(13,13,13,0.97);backdrop-filter:blur(16px);border-bottom:1px solid rgba(255,255,255,0.06);display:flex;align-items:center;justify-content:center;padding:0 24px;height:48px;overflow-x:auto;-webkit-overflow-scrolling:touch;scrollbar-width:none}
  .nav-secondary::-webkit-scrollbar{display:none}
  .nav-secondary a{color:rgba(255,255,255,0.55);font-size:14px;font-weight:400;text-decoration:none;padding:0 18px;height:100%;display:flex;align-items:center;border-bottom:2px solid transparent;transition:all 0.2s;white-space:nowrap;flex-shrink:0}
  .nav-secondary a:hover{color:#fff}
  .nav-secondary a.active{color:#dd3c30;border-color:#dd3c30}
  .btn-red{display:inline-block;background:#dd3c30;color:#fff;padding:13px 50px;border-radius:10px;text-decoration:none;font-weight:500;font-size:15px;transition:transform 0.2s,box-shadow 0.2s;border:none;cursor:pointer;box-shadow:0 6px 20px rgba(39,101,48,0.35)}
  .btn-red:hover{transform:scale(1.04);box-shadow:0 10px 28px rgba(101, 39, 39, 0.45)}
  img{max-width:100%}
  .nav-burger{display:none}
  @media (max-width:900px){
    .nav-links-desktop, .nav-call-desktop{display:none!important}
    .nav-burger{display:flex!important}
    .nav-inner{gap:16px!important}
  }
  @media (max-width:900px){
    .nav-secondary{justify-content:flex-start;padding:0 14px}
  }
  @media (max-width:640px){
    .nav-secondary a{padding:0 12px;font-size:13px}
  }
`;

/* ─── Secondary Nav ─── */
function SecondaryNav({ scrollY }) {
  const anchors = [
    ["#hero", "Overview"],
    ["#story", "Our Story"],
    ["#vision", "Vision"],
    ["#values", "Core Values"],
    ["#team", "Leadership"],
    ["#milestones", "Milestones"],
    ["#why", "Why Us"],
    ["#food-safety-culture", "Food Safety Culture"],
    ["#food-safety-policy", "Food Safety Policy"],
    ["#cta", "Contact"],
  ];
  const [active, setActive] = useState(0);
  const tabRefs = useRef([]);
  useEffect(() => {
    const sections = anchors.map(([id]) => document.querySelector(id));
    const idx = sections.reduceRight((acc, el, i) => {
      if (acc !== -1) return acc;
      if (!el) return -1;
      return el.getBoundingClientRect().top <= 120 ? i : -1;
    }, -1);
    if (idx !== -1) setActive(idx);
  }, [scrollY]);
  useEffect(() => {
    const el = tabRefs.current[active];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [active]);
  return (
    <nav className="nav-secondary">
      {anchors.map(([id, label], i) => (
        <a
          key={id}
          ref={(el) => (tabRefs.current[i] = el)}
          href={id}
          className={i === active ? "active" : ""}
          onClick={() => setActive(i)}
        >
          {label}
        </a>
      ))}
    </nav>
  );
}

/* ─── Hero ─── */
function HeroSection({ scrollY }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 80);
    return () => clearTimeout(t);
  }, []);
  const pY = scrollY * 0.38;
  const tOp = clamp(1 - scrollY / 420, 0, 1);
  const tY = scrollY * 0.22;
  return (
    <section
      id="hero"
      style={{
        position: "relative",
        height: "100vh",
        overflow: "hidden",
        background: "#080808",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          transform: `translateY(${pY}px)`,
        }}
      >
        <img
          src={IMG.bananaTerioka}
          alt=""
          style={{
            width: "100%",
            height: "112%",
            objectFit: "cover",
            filter: "brightness(0.7) saturate(1.1)",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to bottom,rgba(0,0,0,0.15) 0%,rgba(0,0,0,0.55) 60%,rgba(0,0,0,0.92) 100%)",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(ellipse at 65% 55%,rgba(39,101,48,0.3) 0%,transparent 65%)",
          }}
        />
      </div>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          textAlign: "center",
          padding: "0 24px",
          transform: `translateY(${tY}px)`,
          opacity: tOp,
        }}
      >
        <div
          className="ani-fadeup"
          style={{
            animationDelay: "0.1s",
            background: "rgba(255,255,255,0.1)",
            backdropFilter: "blur(10px)",
            borderRadius: 10,
            padding: "6px 30px",
            marginBottom: 28,
            border: "1px solid rgba(255,255,255,0.18)",
            fontSize: 12,
            letterSpacing: 1,
            fontWeight: 700,
            textTransform: "uppercase",
            color: "#ffffff",
          }}
        >
          <i className="bi bi-lightning"></i> About Fruits Garden Trading LLC
        </div>
        <h1
          className="ani-fadeup pf shine-text"
          style={{
            animationDelay: "0.2s",
            fontSize: "clamp(50px,7vw,60px)",
            fontWeight: 600,
            lineHeight: 1.02,
            letterSpacing: -3,
            marginBottom: 18,
          }}
        >
          Two Decades of Trust. Excellence.
          <br />
          Freshness.
        </h1>
        <p
          className="ani-fadeup"
          style={{
            animationDelay: "0.38s",
            fontSize: "clamp(16px,1.8vw,20px)",
            maxWidth: 1100,
            lineHeight: 1.3,
            color: "rgba(255,255,255,0.76)",
            marginBottom: 44,
          }}
        >
          Since 2002, Fruits Garden Trading LLC has been the UAE's premier
          partner for premium fresh fruits and vegetables — built on integrity,
          quality, and an unwavering commitment to the HORECA sector.
        </p>
        <div
          className="ani-fadeup"
          style={{
            animationDelay: "0.52s",
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <a
            href="#story"
            className="btn-green"
            style={{ fontSize: 15, padding: "14px min(45px,4.5vw)" }}
          >
            Our Story
          </a>
          <a
            href="#vision"
            className="btn-ghost"
            style={{ fontSize: 15, padding: "14px min(45px,4.5vw)" }}
          >
            Vision & Mission
          </a>
        </div>
      </div>
      <div
        style={{
          position: "absolute",
          bottom: 30,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
          color: "rgba(255,255,255,0.45)",
          opacity: tOp,
        }}
      >
        <span
          style={{
            fontSize: 10,
            letterSpacing: 2.5,
            textTransform: "uppercase",
          }}
        >
          Scroll
        </span>
        <div
          style={{
            width: 1.5,
            height: 44,
            background: "rgba(255,255,255,0.2)",
            borderRadius: 1,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "45%",
              background: "#dd3c30",
              animation: "scrollDot 1.4s ease-in-out infinite",
            }}
          />
        </div>
      </div>
    </section>
  );
}

/* ─── Stats Banner ─── */
function StatsBanner() {
  const isMobile = useIsMobile();
  const ref = useRef(null);
  const vis = useInView(ref, 0.1);
  const [animPct, setAnimPct] = useState(0);
  useEffect(() => {
    if (vis) {
      let start = null;
      const step = (ts) => {
        if (!start) start = ts;
        const p = Math.min((ts - start) / 1800, 1);
        setAnimPct(p);
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }
  }, [vis]);
  const stats = [
    {
      val: Math.round(animPct * 22) + "+",
      lbl: "Years in Business",
      icon: "bi-calendar3",
    },
    {
      val: Math.round(animPct * 200) + "+",
      lbl: "HORECA Clients Served",
      icon: "bi-handshake",
    },
    {
      val: Math.round(animPct * 15) + "+",
      lbl: "Source Countries",
      icon: "bi-globe-europe-africa",
    },
    {
      val: Math.round(animPct * 500) + "+",
      lbl: "SKUs Available",
      icon: "bi-cart3",
    },
    {
      val: Math.round(animPct * 99) + "%",
      lbl: "On-Time Delivery",
      icon: "bi-truck",
    },
    {
      val: Math.round(animPct * 3) + "x",
      lbl: "Certified Standards",
      icon: "bi-trophy",
    },
  ];
  return (
    <section
      ref={ref}
      style={{
        background: "#0d0d0d",
        padding: "min(64px,6.4vw) 24px",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      <div
        style={{
          maxWidth: 1312,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(6,1fr)",
          gap: 2,
        }}
      >
        {stats.map((s, i) => (
          <div
            key={i}
            style={{
              textAlign: "center",
              padding: "28px 16px",
              borderRight: i < 5 ? "1px solid rgba(255,255,255,0.07)" : "none",
              opacity: vis ? 1 : 0,
              transform: vis ? "translateY(0)" : "translateY(28px)",
              transition: `all 0.7s ease ${i * 0.08}s`,
            }}
          >
            <div style={{ fontSize: 24, marginBottom: 10 }}>
              <i className={`bi ${s.icon}`}></i>
            </div>
            <div
              style={{
                fontSize: "clamp(28px,3.5vw,44px)",
                fontWeight: 900,
                fontFamily: "'Playfair Display',Georgia,serif",
                color: "#dd3c30",
                lineHeight: 1,
                letterSpacing: -1,
              }}
            >
              {s.val}
            </div>
            <div
              style={{
                fontSize: 11,
                color: "rgba(255,255,255,0.4)",
                marginTop: 8,
                letterSpacing: 0.5,
                lineHeight: 1.4,
              }}
            >
              {s.lbl}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─── Story Section ─── */
function StorySection() {
  const wrapRef = useRef(null);
  const prog = useStickyProgress(wrapRef);
  const panels = [
    {
      year: "2002",
      tag: "The Beginning",
      title: "Founded on\nPassion & Purpose.",
      body: "Fruits Garden Trading LLC was established in Dubai with a simple but powerful vision: to bring the world's finest fresh produce to the UAE's hospitality and food service industry. Starting with a small warehouse and a big dream, our founders set the standard for quality that we uphold to this day.",
      img: IMG.warehouse,
      stat: "2002",
      statLbl: "Year Founded",
      col: "#0e1a08",
    },
    {
      year: "2008",
      tag: "Rapid Growth",
      title: "Expanding\nAcross the UAE.",
      body: "Within six years of founding, we had grown our client portfolio to include some of Dubai's most prestigious hotels and restaurant groups. Our reputation for reliability and consistent quality became our most powerful marketing tool — word of mouth spread throughout the HORECA community.",
      img: IMG.globalNetwork,
      stat: "50+",
      statLbl: "Clients by 2008",
      col: "#080e18",
    },
    {
      year: "2015",
      tag: "Global Sourcing",
      title: "World-Class\nSourcing Network.",
      body: "We established direct partnerships with agricultural producers across 15+ countries — India, Philippines, Brazil, Spain, South Africa, Costa Rica, Egypt, and more. This global network ensures year-round availability, competitive pricing, and the finest quality produce regardless of season.",
      img: "/assets/images/latest/2.png",
      stat: "15+",
      statLbl: "Source Countries",
      col: "#180a06",
    },
    {
      year: "2020",
      tag: "Certifications",
      title: "Internationally\nCertified Excellence.",
      body: "Achieving HACCP, ISO 9001, and ISO 22000 certifications marked a new chapter. These internationally recognised standards reflect our commitment to food safety, quality management, and full traceability — from farm to table.",
      img: "/assets/images/latest/5.png",
      stat: "3x",
      statLbl: "Certified Standards",
      col: "#0a0a1a",
    },
    {
      year: "Today",
      tag: "Industry Leader",
      title: "UAE's Trusted\nFFV Partner.",
      body: "Two decades on, Fruits Garden Trading LLC stands as one of the UAE's most trusted fresh fruits and vegetables supply companies. With 200+ HORECA clients, a dedicated logistics fleet, and a team of passionate professionals — we continue to grow, innovate, and deliver.",
      img: "/assets/images/home/5.png",
      stat: "200+",
      statLbl: "Active HORECA Clients",
      col: "#0e1208",
    },
  ];
  const ci = Math.min(panels.length - 1, Math.floor(prog * panels.length));
  const panel = panels[ci];
  return (
    <section
      id="story"
      ref={wrapRef}
      style={{
        height: `${100 * (panels.length + 1.5)}vh`,
        position: "relative",
      }}
    >
      <div
        style={{
          position: "sticky",
          top: 104,
          height: "calc(100vh - 104px)",
          overflow: "hidden",
          background: "#090909",
        }}
      >
        {panels.map((p, i) => (
          <img
            key={i}
            src={p.img}
            alt=""
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              filter: "brightness(0.6) saturate(1.1)",
              opacity: i === ci ? 1 : 0,
              transition: "opacity 0.75s ease",
            }}
          />
        ))}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(135deg,${panel.col}cc 0%,transparent 65%)`,
            transition: "background 0.7s ease",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to left,rgba(0,0,0,0.65) 0%,transparent 60%)",
          }}
        />
        <div
          style={{
            position: "absolute",
            right: "-3%",
            bottom: "5%",
            pointerEvents: "none",
            zIndex: 1,
          }}
        >
          <span
            className="pf"
            style={{
              fontSize: "clamp(140px,20vw,280px)",
              fontWeight: 900,
              color: "rgba(255,255,255,0.04)",
              lineHeight: 1,
              letterSpacing: -6,
            }}
          >
            {panel.year}
          </span>
        </div>
        <div
          style={{
            position: "relative",
            zIndex: 2,
            maxWidth: 1312,
            margin: "0 auto",
            padding: "0 min(64px,6.4vw)",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ maxWidth: 540, color: "#fff" }}>
            <div
              className="sl"
              style={{ display: "flex", alignItems: "center", gap: 10 }}
            >
              <span
                style={{
                  background: "#276530",
                  color: "#fff",
                  padding: "2px 10px",
                  borderRadius: 6,
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: 1,
                }}
              >
                {panel.year}
              </span>
              {panel.tag}
            </div>
            <h2
              className="pf"
              style={{
                fontSize: "clamp(36px,4.8vw,40px)",
                fontWeight: 800,
                lineHeight: 1.08,
                letterSpacing: -2,
                marginBottom: 22,
                whiteSpace: "pre-line",
              }}
            >
              {panel.title}
            </h2>
            <p
              style={{
                fontSize: 16,
                lineHeight: 1.3,
                color: "rgba(255,255,255,0.72)",
                marginBottom: 44,
                maxWidth: 460,
              }}
            >
              {panel.body}
            </p>
            <div style={{ marginBottom: 48 }}>
              <div
                style={{
                  fontSize: 64,
                  fontWeight: 900,
                  fontFamily: "'Playfair Display',Georgia,serif",
                  color: "#dd3c30",
                  lineHeight: 1,
                }}
              >
                {panel.stat}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "rgba(255,255,255,0.45)",
                  marginTop: 6,
                  letterSpacing: 0.5,
                }}
              >
                {panel.statLbl}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {panels.map((_, i) => (
                <div
                  key={i}
                  className={`progress-pill${i === ci ? " active" : ""}`}
                />
              ))}
            </div>
          </div>
        </div>
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            background: "rgba(255,255,255,0.1)",
          }}
        >
          <div
            style={{
              height: "100%",
              background: "#dd3c30",
              width: `${prog * 100}%`,
              transition: "width 0.1s linear",
            }}
          />
        </div>
      </div>
    </section>
  );
}

function MilestonesSection() {
  const wrapRef = useRef(null);
  const prog = useStickyProgress(wrapRef);
  const headRef = useRef(null);
  const headVis = useInView(headRef, 0.1);

  const milestones = [
    {
      year: "2002",
      event: "Company Founded",
      detail:
        "Established in Dubai with a clear vision: supply the finest fresh produce to the UAE's HORECA sector. Starting with a single warehouse and relentless ambition, our founders set a standard of quality that endures to this day.",
      icon: "bi-building",
      color: "#276530",
    },
    {
      year: "2005",
      event: "First Cold Storage",
      detail:
        "We invested in our first temperature-controlled warehouse facility in the Dubai Food District — a landmark that signalled our commitment to cold-chain integrity and year-round freshness guarantees.",
      icon: "bi-snow",
      color: "#0ea5e9",
    },
    {
      year: "2008",
      event: "50+ HORECA Clients",
      detail:
        "Within six years, word had spread across Dubai's hospitality community. We reached 50 active clients including premier hotel groups and restaurant chains — all built entirely on reputation and referrals.",
      icon: "bi-handshake",
      color: "#f59e0b",
    },
    {
      year: "2012",
      event: "Regional Expansion Food Safety - HACCP Certified",
      detail:
        "We have had the HACCP certification since 2012. ISO was obtained in 2018.",
      icon: "bi-map",
      color: "#ec4899",
    },
    {
      year: "2015",
      event: "Global Sourcing",
      detail:
        "We formalised direct partnerships with 15+ agricultural producers across Asia, Europe, Africa, and the Americas. This global network unlocks year-round supply, competitive pricing, and consistently exceptional quality.",
      icon: "bi-globe-europe-africa",
      color: "#8b5cf6",
    },
    {
      year: "2018",
      event: "ADNOC Approved",
      detail:
        "Certified as an approved vendor for the Abu Dhabi National Oil Company — one of the most rigorous and prestigious supplier certifications in the entire region, validating our operational excellence.",
      icon: "bi-star-fill",
      color: "#f97316",
    },
    {
      year: "2018",
      event: "ISO 22000",
      detail:
        "A landmark year: we achieved ISO 22000 certifications across all operations. These globally recognised standards govern every process, every product, and every delivery we make.",
      icon: "bi-trophy",
      color: "#10b981",
    },
    {
      year: "2022",
      event: "200+ Active Clients",
      detail:
        "We proudly crossed the milestone of 200 active HORECA clients across the UAE — hotels, restaurants, catering groups, and institutional buyers who trust us to deliver every single day without exception.",
      icon: "bi-graph-up-arrow",
      color: "#276530",
    },
    {
      year: "2024+",
      event: "The Future",
      detail:
        "Our journey continues. We are expanding our cold-chain infrastructure, deepening global sourcing partnerships, and investing in digital systems — all to deliver greater quality and efficiency to every client we serve.",
      icon: "bi-rocket-takeoff",
      color: "#dd3c30",
    },
  ];

  const N = milestones.length;
  const floatIdx = prog * N;
  const ci = Math.min(N - 1, Math.floor(floatIdx));

  // How many cards are visible at once — 2 full + peek of 3rd
  const CARD_WIDTH = 480;
  const CARD_GAP = 24;
  const CARD_STEP = CARD_WIDTH + CARD_GAP;

  // Translate so active card is centered (offset to show prev card peeking)
  const translateX = clamp(ci * CARD_STEP - 120, 0, (N - 1) * CARD_STEP);

  return (
    <section
      id="milestones"
      ref={wrapRef}
      style={{ height: `${N * 120 + 200}vh`, position: "relative" }}
    >
      <div
        style={{
          position: "sticky",
          top: 0,
          height: "100vh",
          overflow: "hidden",
          background: "#080f0a",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {/* Animated radial bg */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background: `radial-gradient(ellipse at ${10 + prog * 80}% 50%, rgba(39,101,48,0.15) 0%, transparent 55%)`,
            transition: "background 0.2s linear",
          }}
        />

        {/* Subtle grid */}
        <svg
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            opacity: 0.03,
            pointerEvents: "none",
          }}
        >
          {Array.from({ length: 14 }).map((_, i) => (
            <line
              key={i}
              x1={`${i * 7.7}%`}
              y1="0"
              x2={`${i * 7.7}%`}
              y2="100%"
              stroke="#dd3c30"
              strokeWidth="1"
            />
          ))}
        </svg>

        {/* Header */}
        <div ref={headRef} style={{ padding: "0 min(64px,6.4vw)", marginBottom: 44 }}>
          <div
            className="sl"
            style={{
              opacity: headVis ? 1 : 0,
              transform: headVis ? "none" : "translateY(20px)",
              transition: "all 0.8s ease",
            }}
          >
            Our Journey
          </div>
          <h2
            className="pf"
            style={{
              fontSize: "clamp(36px,4.5vw,58px)",
              fontWeight: 900,
              letterSpacing: -2,
              color: "#fff",
              lineHeight: 1.05,
              opacity: headVis ? 1 : 0,
              transform: headVis ? "none" : "translateY(30px)",
              transition: "all 0.9s cubic-bezier(.22,1,.36,1) 0.1s",
            }}
          >
            Two Decades of <span className="shimmer-green">Milestones.</span>
          </h2>
        </div>

        {/* Card strip */}
        <div
          style={{
            padding: "0 min(64px,6.4vw)",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: CARD_GAP,
              transform: `translateX(-${translateX}px)`,
              transition: "transform 0.5s cubic-bezier(.22,1,.36,1)",
              willChange: "transform",
            }}
          >
            {milestones.map((m, i) => {
              const dist = Math.abs(i - floatIdx);
              const isActive = i === ci;
              const isAdjacent = dist < 1.5 && !isActive;
              const scale = isActive ? 1 : isAdjacent ? 0.94 : 0.88;
              const opacity = isActive ? 1 : isAdjacent ? 0.65 : 0.35;

              return (
                <div key={i} style={{ flex: `0 0 ${CARD_WIDTH}px` }}>
                  <div
                    style={{
                      background: isActive
                        ? `linear-gradient(135deg, ${m.color}28 0%, ${m.color}0d 100%)`
                        : "rgba(255,255,255,0.025)",
                      border: `1px solid ${isActive ? m.color + "70" : "rgba(255,255,255,0.07)"}`,
                      borderRadius: 24,
                      padding: "min(40px,4vw) 36px",
                      transform: `scale(${scale})`,
                      opacity: opacity,
                      transition: "all 0.5s cubic-bezier(.22,1,.36,1)",
                      transformOrigin: "left center",
                      boxShadow: isActive ? `0 24px 80px ${m.color}30` : "none",
                      minHeight: 300,
                    }}
                  >
                    {/* Top row: icon + year */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 20,
                      }}
                    >
                      <span style={{ fontSize: 44 }}>
                        <i
                          className={`bi ${m.icon}`}
                          style={{ color: "#fff" }}
                        ></i>
                      </span>
                      <div
                        style={{
                          background: isActive
                            ? m.color
                            : "rgba(255,255,255,0.08)",
                          color: "#fff",
                          padding: "5px 16px",
                          borderRadius: 20,
                          fontSize: 12,
                          fontWeight: 800,
                          letterSpacing: 2,
                          fontFamily: "'DM Sans',sans-serif",
                          transition: "background 0.4s",
                        }}
                      >
                        {m.year}
                      </div>
                    </div>

                    {/* Event title */}
                    <h3
                      className="pf"
                      style={{
                        fontSize: 26,
                        fontWeight: 800,
                        color: "#fff",
                        marginBottom: 14,
                        lineHeight: 1.2,
                      }}
                    >
                      {m.event}
                    </h3>

                    {/* Divider */}
                    <div
                      style={{
                        width: isActive ? 52 : 24,
                        height: 2,
                        background: isActive
                          ? m.color
                          : "rgba(255,255,255,0.15)",
                        borderRadius: 2,
                        marginBottom: 18,
                        transition: "all 0.5s ease",
                      }}
                    />

                    {/* Detail text — full readable body */}
                    <p
                      style={{
                        fontSize: 15,
                        color: isActive
                          ? "rgba(255,255,255,0.78)"
                          : "rgba(255,255,255,0.45)",
                        lineHeight: 1.3,
                        fontFamily: "'DM Sans',sans-serif",
                        transition: "color 0.4s",
                      }}
                    >
                      {m.detail}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Index indicator */}
        <div
          style={{
            padding: "28px min(64px,6.4vw) 0",
            display: "flex",
            alignItems: "center",
            gap: 16,
          }}
        >
          <div style={{ display: "flex", gap: 7 }}>
            {milestones.map((m, i) => (
              <div
                key={i}
                style={{
                  height: 5,
                  width: i === ci ? 28 : 5,
                  borderRadius: 3,
                  background: i === ci ? m.color : "rgba(255,255,255,0.15)",
                  transition: "all 0.4s ease",
                }}
              />
            ))}
          </div>
          <span
            style={{
              fontSize: 12,
              color: "rgba(255,255,255,0.35)",
              fontFamily: "'DM Sans',sans-serif",
              letterSpacing: 1,
            }}
          >
            {ci + 1} / {N}
          </span>
        </div>

        {/* Big watermark year */}
        <div
          style={{
            position: "absolute",
            right: "-2%",
            bottom: "4%",
            pointerEvents: "none",
            zIndex: 0,
          }}
        >
          <span
            className="pf"
            style={{
              fontSize: "clamp(160px,22vw,320px)",
              fontWeight: 900,
              color: "rgba(255,255,255,0.025)",
              lineHeight: 1,
              letterSpacing: -8,
            }}
          >
            {milestones[ci].year}
          </span>
        </div>

        {/* Progress bar */}
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            background: "rgba(255,255,255,0.05)",
          }}
        >
          <div
            style={{
              height: "100%",
              background: `linear-gradient(90deg,#276530,${milestones[ci].color})`,
              width: `${prog * 100}%`,
              transition: "width 0.08s linear",
            }}
          />
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   CERTIFICATIONS — FIXED: Compact 2×2 grid
   cards, tighter padding, smaller type
───────────────────────────────────────────── */
function CertificationsSection() {
  const isMobile = useIsMobile();
  const ref = useRef(null);
  const vis = useInView(ref, 0.08);

  const certs = [
    {
      name: "HACCP 2020",
      full: "Hazard Analysis & Critical Control Points",
      icon: "bi-shield-check",
      color: "#276530",
      light: "rgba(39,101,48,0.1)",
      desc: "A systematic, preventive approach to food safety covering physical, chemical, and biological hazards — governing every step of our supply chain from source to delivery.",
    },
    {
      name: "ISO 9001:2015",
      full: "Quality Management Systems",
      icon: "bi-check-circle-fill",
      color: "#0ea5e9",
      light: "rgba(14,165,233,0.1)",
      desc: "The international standard for quality management ensuring consistent delivery of products that meet both customer requirements and applicable regulatory standards.",
    },
    {
      name: "ISO 22000:2018",
      full: "Food Safety Management",
      icon: "bi-lightning",
      color: "#f59e0b",
      light: "rgba(245,158,11,0.1)",
      desc: "Covers all organisations in the food supply chain, specifying requirements for a fully-integrated food safety management system with complete traceability.",
    },
    {
      name: "ADNOC",
      full: "Approved Vendor Certification",
      icon: "bi-star-fill",
      color: "#ec4899",
      light: "rgba(236,72,153,0.1)",
      desc: "Certified as an approved vendor for ADNOC — one of the most rigorous and prestigious supplier certifications in the entire Middle East region.",
    },
  ];

  return (
    <section
      id="certifications"
      ref={ref}
      style={{
        background: "#080808",
        padding: "min(96px,9.6vw) 24px",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Ghost text bg */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%,-50%)",
          pointerEvents: "none",
          zIndex: 0,
          whiteSpace: "nowrap",
        }}
      >
        <span
          className="pf"
          style={{
            fontSize: "16vw",
            fontWeight: 900,
            color: "rgba(255,255,255,0.018)",
            lineHeight: 1,
          }}
        >
          CERTIFIED
        </span>
      </div>

      <div
        style={{
          maxWidth: 1160,
          margin: "0 auto",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              background: "rgba(221,60,48,0.08)",
              border: "1px solid rgba(221,60,48,0.18)",
              borderRadius: 100,
              padding: "5px 18px",
              marginBottom: 20,
              opacity: vis ? 1 : 0,
              transition: "all 0.8s ease",
            }}
          >
            <div
              style={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                background: "#dd3c30",
                animation: "orbGrow 2s infinite",
              }}
            />
            <span className="sl" style={{ marginBottom: 0, color: "#dd3c30" }}>
              Standards & Trust
            </span>
          </div>
          <h2
            className="pf"
            style={{
              fontSize: "clamp(34px,4.5vw,56px)",
              fontWeight: 900,
              letterSpacing: -2.5,
              color: "#fff",
              lineHeight: 1.05,
              marginBottom: 14,
              opacity: vis ? 1 : 0,
              transform: vis ? "none" : "translateY(36px)",
              transition: "all 0.9s cubic-bezier(.22,1,.36,1) 0.1s",
            }}
          >
            Internationally Certified.
            <br />
            <span className="shimmer-green">Uncompromisingly Safe.</span>
          </h2>
          <p
            style={{
              fontSize: 15,
              color: "rgba(255,255,255,0.48)",
              maxWidth: 520,
              margin: "0 auto",
              lineHeight: 1.45,
              fontFamily: "'DM Sans',sans-serif",
              opacity: vis ? 1 : 0,
              transform: vis ? "none" : "translateY(16px)",
              transition: "all 0.9s ease 0.2s",
            }}
          >
            We operate under the world's most rigorous food safety and quality
            standards — because our clients deserve total assurance, every
            delivery.
          </p>
        </div>

        {/* Compact 2×2 grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(2,1fr)",
            gap: 14,
          }}
        >
          {certs.map((c, i) => (
            <div
              key={i}
              style={{
                position: "relative",
                borderRadius: 18,
                overflow: "hidden",
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.07)",
                padding: "28px 28px 28px 32px",
                opacity: vis ? 1 : 0,
                transform: vis ? "none" : `translateY(60px) scale(0.96)`,
                transition: `all 0.8s cubic-bezier(.22,1,.36,1) ${0.08 + i * 0.1}s`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `linear-gradient(135deg, ${c.color}15, ${c.color}05)`;
                e.currentTarget.style.borderColor = `${c.color}45`;
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = `0 20px 60px ${c.color}20`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.03)";
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)";
                e.currentTarget.style.transform = "none";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              {/* Left accent bar */}
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 3,
                  background: `linear-gradient(to bottom, ${c.color}, transparent)`,
                }}
              />

              <div
                style={{ display: "flex", gap: 16, alignItems: "flex-start" }}
              >
                {/* Icon */}
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 14,
                    background: c.light,
                    border: `1px solid ${c.color}25`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 24,
                    flexShrink: 0,
                  }}
                >
                  <i className={`bi ${c.icon}`} style={{ color: "#fff" }}></i>
                </div>
                <div style={{ flex: 1 }}>
                  {/* Name + status row */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 4,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        letterSpacing: 2.5,
                        fontWeight: 800,
                        color: c.color,
                        textTransform: "uppercase",
                        fontFamily: "'DM Sans',sans-serif",
                      }}
                    >
                      {c.name}
                    </span>
                  </div>
                  {/* Full name */}
                  <div
                    className="pf"
                    style={{
                      fontSize: 17,
                      fontWeight: 800,
                      color: "#fff",
                      lineHeight: 1.25,
                      marginBottom: 10,
                    }}
                  >
                    {c.full}
                  </div>
                  {/* Desc */}
                  <p
                    style={{
                      fontSize: 13,
                      color: "rgba(255,255,255,0.5)",
                      lineHeight: 1.45,
                      fontFamily: "'DM Sans',sans-serif",
                    }}
                  >
                    {c.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Core Values (sticky scroll) ─── */
function CoreValuesSection() {
  const wrapRef = useRef(null);
  const prog = useStickyProgress(wrapRef);
  const values = [
    {
      name: "Quality First",
      number: "01",
      icon: "bi-award",
      color: "#276530",
      bg: "#e8f5e9",
      desc: "We never compromise on quality. Every product is carefully selected, graded, and inspected before it reaches our clients — because quality isn't a feature, it's our foundation.",
      keywords: ["Graded", "Inspected", "Certified", "Traceable"],
    },
    {
      name: "Integrity",
      number: "02",
      icon: "bi-hand-thumbs-up",
      color: "#0ea5e9",
      bg: "#e0f2fe",
      desc: "Honest pricing, transparent communication, and reliable service. Every relationship we build is grounded in trust and mutual respect — no exceptions, ever.",
      keywords: ["Honest", "Transparent", "Trustworthy", "Fair"],
    },
    {
      name: "Reliability",
      number: "03",
      icon: "bi-clock",
      color: "#f59e0b",
      bg: "#fef3c7",
      desc: "Our clients run their businesses on the assumption that we will deliver. We take that responsibility seriously — on-time, every time, without exception.",
      keywords: ["Punctual", "Consistent", "Dependable", "Committed"],
    },
    {
      name: "Innovation",
      number: "04",
      icon: "bi-lightbulb",
      color: "#8b5cf6",
      bg: "#ede9fe",
      desc: "From cold-chain logistics to digital order management, we continuously invest in technology and processes that make our service faster, smarter, and more efficient.",
      keywords: ["Digital", "Efficient", "Modern", "Future-Ready"],
    },
    {
      name: "Sustainability",
      number: "05",
      icon: "bi-tree",
      color: "#10b981",
      bg: "#d1fae5",
      desc: "We are committed to responsible sourcing and minimising environmental impact — working with farmers who share our values and packaging solutions that respect our planet.",
      keywords: ["Eco", "Ethical", "Responsible", "Green"],
    },
  ];
  const N = values.length;
  const ci = Math.min(N - 1, Math.floor(prog * N));
  const v = values[ci];
  return (
    <section
      id="values"
      ref={wrapRef}
      style={{ height: `${N * 100 + 200}vh`, position: "relative" }}
    >
      <div
        style={{
          position: "sticky",
          top: 0,
          height: "100vh",
          overflow: "hidden",
          background: "#fff",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            zIndex: 0,
            background: `radial-gradient(ellipse at 68% 50%, ${v.bg}cc 0%, transparent 58%)`,
            transition: "background 0.7s ease",
          }}
        />
        <div
          style={{
            position: "absolute",
            right: "-3%",
            top: "50%",
            transform: "translateY(-50%)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        >
          <span
            className="pf"
            style={{
              fontSize: "clamp(200px,28vw,400px)",
              fontWeight: 900,
              color: v.color,
              opacity: 0.04,
              lineHeight: 1,
              transition: "color 0.6s ease",
            }}
          >
            {v.number}
          </span>
        </div>
        <div
          style={{
            position: "relative",
            zIndex: 1,
            maxWidth: 1312,
            margin: "0 auto",
            padding: "0 min(80px,8vw)",
            height: "100%",
            display: "flex",
            alignItems: "center",
            gap: 72,
          }}
        >
          {/* Left */}
          <div style={{ flex: 1 }}>
            <div
              className="sl-dark"
              style={{ color: v.color, transition: "color 0.5s" }}
            >
              Our Core Values
            </div>
            <h2
              className="pf"
              style={{
                fontSize: "clamp(32px,3.5vw,48px)",
                fontWeight: 900,
                letterSpacing: -2,
                color: "#0d0d0d",
                lineHeight: 1.08,
                marginBottom: 36,
              }}
            >
              What We{" "}
              <span style={{ color: v.color, transition: "color 0.5s" }}>
                Stand For.
              </span>
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              {values.map((vv, i) => {
                const isActive = i === ci;
                return (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      padding: "16px 18px",
                      borderRadius: 14,
                      background: isActive ? vv.bg : "transparent",
                      border: `1.5px solid ${isActive ? vv.color + "50" : "transparent"}`,
                      transition: "all 0.5s cubic-bezier(.22,1,.36,1)",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    {isActive && (
                      <div
                        style={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: 3,
                          background: vv.color,
                          borderRadius: "3px 0 0 3px",
                          animation: "fadeIn 0.3s ease",
                        }}
                      />
                    )}
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 800,
                        color: isActive ? vv.color : "#ccc",
                        fontFamily: "'DM Sans',sans-serif",
                        letterSpacing: 1,
                        minWidth: 22,
                        transition: "color 0.4s",
                      }}
                    >
                      {vv.number}
                    </span>
                    <span
                      style={{
                        fontSize: isActive ? 22 : 17,
                        transition: "font-size 0.4s ease",
                      }}
                    >
                      <i className={`bi ${vv.icon}`}></i>
                    </span>
                    <span
                      style={{
                        fontSize: 15,
                        fontWeight: isActive ? 800 : 500,
                        color: isActive ? vv.color : "#555",
                        fontFamily: isActive
                          ? "'Playfair Display',serif"
                          : "'DM Sans',sans-serif",
                        transition: "all 0.4s",
                        flex: 1,
                      }}
                    >
                      {vv.name}
                    </span>
                    {isActive && (
                      <span
                        style={{
                          fontSize: 10,
                          letterSpacing: 1.5,
                          fontWeight: 700,
                          color: vv.color,
                          textTransform: "uppercase",
                          fontFamily: "'DM Sans',sans-serif",
                          animation: "fadeIn 0.3s ease",
                          opacity: 0.7,
                        }}
                      >
                        Active
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          {/* Right card */}
          <div style={{ flex: 0.85 }}>
            <div
              style={{
                borderRadius: 32,
                background: v.bg,
                transition: "background 0.6s ease",
                boxShadow: `0 32px 100px ${v.color}22`,
                padding: "min(48px,4.8vw) min(44px,4.4vw)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  fontSize: 72,
                  marginBottom: 22,
                  animation: "floatY 3s ease-in-out infinite",
                  display: "inline-block",
                }}
              >
                <i className={`bi ${v.icon}`}></i>
              </div>
              <div
                className="pf"
                style={{
                  fontSize: 100,
                  fontWeight: 900,
                  color: v.color,
                  opacity: 0.07,
                  position: "absolute",
                  right: 16,
                  top: 10,
                  lineHeight: 1,
                  transition: "color 0.6s",
                }}
              >
                {v.number}
              </div>
              <h3
                className="pf"
                style={{
                  fontSize: "clamp(28px,3vw,40px)",
                  fontWeight: 900,
                  color: v.color,
                  lineHeight: 1.1,
                  marginBottom: 12,
                  transition: "color 0.5s",
                }}
              >
                {v.name}
              </h3>
              <div
                style={{
                  width: 48,
                  height: 3,
                  background: v.color,
                  borderRadius: 2,
                  marginBottom: 18,
                  transition: "background 0.5s",
                }}
              />
              <p
                style={{
                  fontSize: 14.5,
                  color: "#444",
                  lineHeight: 1.3,
                  fontFamily: "'DM Sans',sans-serif",
                  marginBottom: 24,
                }}
              >
                {v.desc}
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                {v.keywords.map((k, i) => (
                  <span
                    key={i}
                    style={{
                      background: v.color + "18",
                      border: `1px solid ${v.color}40`,
                      borderRadius: 20,
                      padding: "3px 12px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: v.color,
                      letterSpacing: 1.5,
                      textTransform: "uppercase",
                      fontFamily: "'DM Sans',sans-serif",
                      animation: `scaleIn 0.4s cubic-bezier(.22,1,.36,1) ${i * 0.08}s both`,
                    }}
                  >
                    {k}
                  </span>
                ))}
              </div>
              <div style={{ display: "flex", gap: 7, marginTop: 24 }}>
                {values.map((_, i) => (
                  <div
                    key={i}
                    style={{
                      height: 5,
                      width: i === ci ? 26 : 5,
                      borderRadius: 3,
                      background: i === ci ? v.color : v.color + "30",
                      transition: "all 0.4s ease",
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            background: "rgba(0,0,0,0.05)",
          }}
        >
          <div
            style={{
              height: "100%",
              background: v.color,
              width: `${prog * 100}%`,
              transition: "width 0.08s linear, background 0.5s ease",
            }}
          />
        </div>
      </div>
    </section>
  );
}

/* ─── Leadership ─── */
function LeadershipSection() {
  const ref = useRef(null);
  const vis = useInView(ref, 0.1);
  const [hov, setHov] = useState(null);

  const TEAM_PATH = "/assets/images/company/team";

  // size: "lg" = same size as first 3 tiers, "sm" = smaller (Department Heads)
  const tiers = [
    {
      label: "Director",
      size: "lg",
      members: [
        {
          name: "Mohammed Bidshahri",
          role: "Managing Director",
          dept: "Director",
          img: `${TEAM_PATH}/Mohammed Bidshahri.png`,
        },
      ],
    },
    {
      label: "Executive Leadership",
      size: "lg",
      members: [
        {
          name: "Mahmoud Sulaimani",
          role: "General Manager",
          dept: "Executive",
          img: `${TEAM_PATH}/Mahmoud Sulaimani.jpeg`,
        },
        {
          name: "Sunil Kumar",
          role: "Finance Manager",
          dept: "Finance",
          img: `${TEAM_PATH}/Sunil Kumar.png`,
        },
      ],
    },
    {
      label: "Senior Management",
      size: "lg",
      members: [
        {
          name: "Farshad Behrouzi",
          role: "Business Development Manager",
          dept: "Procurement",
          img: `${TEAM_PATH}/Farshad Behrouzi.jpeg`,
        },
        {
          name: "Najeeb Ebrahim",
          role: "Head of Accounts",
          dept: "Accounts",
          img: `${TEAM_PATH}/Najeeb Emrahim.png`,
        },
        {
          name: "Abid",
          role: "Assistant General Manager & Sales Head",
          dept: "Sales",
          img: `${TEAM_PATH}/Abid.png`,
        },
      ],
    },
    {
      label: "Department Heads",
      size: "sm",
      members: [
        {
          name: "Mohammed Koya",
          role: "Operations Head",
          dept: "Operations",
          img: `${TEAM_PATH}/Mohammed Koya.jpeg`,
        },
        {
          name: "Manzar Bashir",
          role: "Purchase Head",
          dept: "Purchasing",
          img: `${TEAM_PATH}/Manzar Bashir.jpeg`,
        },
        {
          name: "Chitra Thapa",
          role: "Customer Service TL",
          dept: "Customer",
          img: `${TEAM_PATH}/Chitra Thapa.jpeg`,
        },
        {
          name: "Muhammed Salih",
          role: "Sales Coordinator TL",
          dept: "Sales",
          img: `${TEAM_PATH}/Muhammed Salih.jpeg`,
        },
        {
          name: "Vishnu Sai",
          role: "Quality Assurance Manager",
          dept: "Quality",
          img: `${TEAM_PATH}/Vishnu Sai.jpeg`,
        },
      ],
    },
  ];

  // Card widths per size tier
  const CARD_WIDTH = { lg: 250, sm: 220 };

  let flatIndex = -1;

  return (
    <section
      id="team"
      ref={ref}
      style={{
        background: "#0d0a0a",
        padding: "min(100px,10vw) 24px 24px",
        overflow: "hidden",
        paddingBottom: 72,
      }}
    >
      <div style={{ maxWidth: 1312, margin: "0 auto" }}>
        <div
          style={{
            textAlign: "center",
            marginBottom: 72,
            opacity: vis ? 1 : 0,
            transform: vis ? "translateY(0)" : "translateY(32px)",
            transition: "all 0.85s ease",
          }}
        >
          <div className="sl">Leadership Team</div>
          <h2
            className="pf"
            style={{
              fontSize: "clamp(38px,5vw,56px)",
              fontWeight: 800,
              letterSpacing: -2.5,
              color: "#fff",
              lineHeight: 1.05,
              marginBottom: 16,
            }}
          >
            The People Behind
            <br />
            <span style={{ color: "#dd3c30" }}>Our Excellence.</span>
          </h2>
          <p
            style={{
              fontSize: 17,
              color: "rgba(255,255,255,0.55)",
              maxWidth: 860,
              margin: "0 auto",
              lineHeight: 1.3,
              fontFamily: "'DM Sans',sans-serif",
            }}
          >
            Guided by decades of industry experience, our leadership team is
            committed to upholding the values and vision that have made Fruits
            Garden a trusted name in the UAE.
          </p>
        </div>

        {tiers.map((tier, tIdx) => (
          <div key={tier.label} style={{ marginTop: 20 }}>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: 16,
              }}
            >
              {tier.members.map((m) => {
                flatIndex++;
                const key = `${tier.label}-${m.name}`;
                const w = CARD_WIDTH[tier.size];
                return (
                  <div
                    key={key}
                    onMouseEnter={() => setHov(key)}
                    onMouseLeave={() => setHov(null)}
                    style={{
                      position: "relative",
                      borderRadius: 20,
                      overflow: "hidden",
                      cursor: "pointer",
                      width: w,
                      aspectRatio: "3/4",
                      flex: `0 0 ${w}px`,
                      opacity: vis ? 1 : 0,
                      transform: vis ? "translateY(0)" : "translateY(50px)",
                      transition: `all 0.7s ease ${flatIndex * 0.06}s`,
                    }}
                  >
                    <img
                      src={m.img}
                      alt={m.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transform: hov === key ? "scale(1.07)" : "scale(1)",
                        transition: "transform 0.65s cubic-bezier(.22,1,.36,1)",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background:
                          hov === key
                            ? "linear-gradient(to top,rgba(0,0,0,0.92) 0%,rgba(0,0,0,0.35) 55%,transparent 100%)"
                            : "linear-gradient(to top,rgba(0,0,0,0.8) 0%,rgba(0,0,0,0.15) 55%,transparent 100%)",
                        transition: "background 0.4s ease",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        top: 18,
                        right: 18,
                        background: "rgba(39,101,48,0.85)",
                        backdropFilter: "blur(8px)",
                        borderRadius: 8,
                        padding: "4px 10px",
                        fontSize: 11,
                        color: "#fff",
                        fontWeight: 600,
                      }}
                    >
                      {m.dept}
                    </div>
                    <div
                      style={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        padding: 22,
                      }}
                    >
                      <div
                        className="pf"
                        style={{
                          fontSize: 18,
                          fontWeight: 800,
                          color: "#fff",
                          marginBottom: 4,
                          lineHeight: 1.2,
                        }}
                      >
                        {m.name}
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          color: "rgba(255,255,255,0.65)",
                          fontFamily: "'DM Sans',sans-serif",
                          lineHeight: 1.3,
                        }}
                      >
                        {m.role}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function CultureSection() {
  const isMobile = useIsMobile();
  const ref = useRef(null);
  const vis = useInView(ref, 0.06);
  const [animPct, setAnimPct] = useState(0);
  const [hovIdx, setHovIdx] = useState(null);

  useEffect(() => {
    if (!vis) return;
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / 2000, 1);
      setAnimPct(p);
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [vis]);

  const pillars = [
    {
      icon: "bi-tree",
      title: "Sustainability First",
      desc: "We partner only with certified farms meeting international environmental standards. Responsible sourcing is our commitment to the planet and future generations.",
      stat: 100,
      suffix: "%",
      statLabel: "Certified Farm Partners",
      color: "#276530",
      accent: "#dd3c30",
    },
    {
      icon: "bi-people",
      title: "People Over Profit",
      desc: "Our 80+ team members are treated as family. From warehouse staff to account managers, every person drives our promise of exceptional daily service.",
      stat: 80,
      suffix: "+",
      statLabel: "Dedicated Team Members",
      color: "#3b82f6",
      accent: "#93c5fd",
    },
    {
      icon: "bi-eyedropper",
      title: "Relentless Quality",
      desc: "Every shipment undergoes rigorous inspection against Grade A standards. Our HACCP-certified processes ensure zero compromise from farm to fork.",
      stat: 99.8,
      suffix: "%",
      statLabel: "Quality Pass Rate",
      color: "#f59e0b",
      accent: "#fcd34d",
    },
    {
      icon: "bi-globe-europe-africa",
      title: "Community Impact",
      desc: "By sourcing from smallholder farms across 15 countries, we directly contribute to rural livelihoods and sustainable agricultural development worldwide.",
      stat: 15,
      suffix: "+",
      statLabel: "Countries Supported",
      color: "#ec4899",
      accent: "#f9a8d4",
    },
  ];

  return (
    <section
      id="culture"
      ref={ref}
      style={{
        background: "#0a1508",
        padding: "min(96px,9.6vw) 24px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Diagonal stripe bg */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          opacity: 0.02,
          backgroundImage:
            "repeating-linear-gradient(45deg,#dd3c30 0,#dd3c30 1px,transparent 0,transparent 28px)",
          backgroundSize: "40px 40px",
        }}
      />

      {/* Floating orbs */}
      {[
        { x: "8%", y: "25%", size: 260, delay: "0s" },
        { x: "85%", y: "65%", size: 320, delay: "1.5s" },
      ].map((orb, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            left: orb.x,
            top: orb.y,
            width: orb.size,
            height: orb.size,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(39,101,48,0.1) 0%, transparent 70%)",
            transform: "translate(-50%,-50%)",
            animation: `orbGrow 4s ease-in-out infinite ${orb.delay}`,
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
      ))}

      <div
        style={{
          maxWidth: 1160,
          margin: "0 auto",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <div
            className="sl"
            style={{
              opacity: vis ? 1 : 0,
              transform: vis ? "none" : "translateY(20px)",
              transition: "all 0.8s ease",
            }}
          >
            What Drives Us
          </div>
          <h2
            className="pf"
            style={{
              fontSize: "clamp(34px,4.2vw,56px)",
              fontWeight: 900,
              letterSpacing: -2.5,
              color: "#fff",
              lineHeight: 1.0,
              opacity: vis ? 1 : 0,
              transform: vis ? "none" : "translateY(36px)",
              transition: "all 0.9s cubic-bezier(.22,1,.36,1) 0.1s",
            }}
          >
            Our <span className="shimmer-green">Culture</span> & Purpose
          </h2>
        </div>

        {/* Compact 2×2 grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(2,1fr)",
            gap: 14,
          }}
        >
          {pillars.map((p, i) => {
            const isHov = hovIdx === i;
            const animVal =
              p.stat <= 100
                ? Math.round(animPct * p.stat * 10) / 10
                : Math.round(animPct * p.stat);

            return (
              <div
                key={i}
                onMouseEnter={() => setHovIdx(i)}
                onMouseLeave={() => setHovIdx(null)}
                style={{
                  borderRadius: 20,
                  padding: "32px 30px",
                  background: isHov
                    ? `linear-gradient(135deg, ${p.color}18, ${p.color}07)`
                    : "rgba(255,255,255,0.025)",
                  border: `1px solid ${isHov ? p.color + "45" : "rgba(255,255,255,0.06)"}`,
                  transition: "all 0.45s cubic-bezier(.22,1,.36,1)",
                  opacity: vis ? 1 : 0,
                  transform: vis ? "none" : `translateY(${50 + i * 16}px)`,
                  transitionDelay: vis ? `${i * 0.1}s` : "0s",
                  boxShadow: isHov ? `0 24px 80px ${p.color}18` : "none",
                  cursor: "default",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* Corner accent */}
                <div
                  style={{
                    position: "absolute",
                    top: -40,
                    right: -40,
                    width: 140,
                    height: 140,
                    borderRadius: "50%",
                    background: `radial-gradient(circle, ${p.color}${isHov ? "25" : "10"} 0%, transparent 70%)`,
                    transition: "all 0.45s ease",
                  }}
                />

                {/* Top row: icon + stat */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    marginBottom: 16,
                  }}
                >
                  <span
                    style={{
                      fontSize: 36,
                      display: "inline-block",
                      color: "#fff",
                      transform: isHov
                        ? "scale(1.08) translateY(-2px)"
                        : "scale(1)",
                      transition: "transform 0.4s cubic-bezier(.22,1,.36,1)",
                    }}
                  >
                    <i className={`bi ${p.icon}`}></i>
                  </span>
                  <div style={{ textAlign: "right" }}>
                    <div
                      className="pf"
                      style={{
                        fontSize: "clamp(28px,2.8vw,36px)",
                        fontWeight: 900,
                        color: p.color,
                        lineHeight: 1,
                        textShadow: isHov ? `0 0 30px ${p.color}50` : "none",
                        transition: "text-shadow 0.4s",
                      }}
                    >
                      {animVal}
                      {p.suffix}
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: "rgba(255,255,255,0.3)",
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                        fontFamily: "'DM Sans',sans-serif",
                        maxWidth: 100,
                        lineHeight: 1.3,
                      }}
                    >
                      {p.statLabel}
                    </div>
                  </div>
                </div>

                {/* Title */}
                <h3
                  className="pf"
                  style={{
                    fontSize: 19,
                    fontWeight: 800,
                    color: "#fff",
                    marginBottom: 8,
                    lineHeight: 1.25,
                  }}
                >
                  {p.title}
                </h3>

                {/* Animated underline */}
                <div
                  style={{
                    width: isHov ? 44 : 22,
                    height: 2,
                    background: p.color,
                    borderRadius: 2,
                    marginBottom: 14,
                    transition: "width 0.45s ease",
                  }}
                />

                {/* Desc */}
                <p
                  style={{
                    fontSize: 13.5,
                    color: "rgba(255,255,255,0.52)",
                    lineHeight: 1.45,
                    fontFamily: "'DM Sans',sans-serif",
                  }}
                >
                  {p.desc}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─── Certificate Downloads ─── */
// function CertificateDownloadsSection() {
//   const ref = useRef(null);
//   const vis = useInView(ref, 0.06);
//   const [activeDoc, setActiveDoc] = useState(null); // null = none open
//   const [hovIdx, setHovIdx] = useState(null);

//   const docs = [
//     {
//       id: "icv",
//       label: "ICV Certificate",
//       sublabel: "In-Country Value · FGT",
//       file: "ICV Certificate FGT.pdf",
//       icon: "bi-trophy",
//       color: "#f59e0b",
//       light: "rgba(245,158,11,0.1)",
//       tag: "ADNOC",
//     },
//     {
//       id: "ajman",
//       label: "Supplier Referral Letter",
//       sublabel: "Customer Appreciation",
//       file: "Ajman hotel.pdf",
//       icon: "bi-building-fill",
//       color: "#0ea5e9",
//       light: "rgba(14,165,233,0.1)",
//       tag: "HORECA",
//     },
//     {
//       id: "fgt",
//       label: "Fruits Garden Trading LLC",
//       sublabel: "May 2025 – Apr 2026",
//       file: "Fruits Garden Trading LLC (May 2025 to Apr 2026).pdf",
//       icon: "bi-tree",
//       color: "#dd3c30",
//       light: "rgba(221,60,48,0.1)",
//       tag: "Sustainability",
//     },
//     {
//       id: "haccp",
//       label: "HACCP Certificate",
//       sublabel: "Food Safety Standard 2020",
//       file: "HACCP.pdf",
//       icon: "bi-shield-check",
//       color: "#276530",
//       light: "rgba(39,101,48,0.12)",
//       tag: "Certified",
//     },
//     {
//       id: "inspection",
//       label: "Inspection Certificate",
//       sublabel: "Food Safety Department (Dubai Municipality)",
//       file: "Inspection_537319_1146857.pdf",
//       icon: "bi-search",
//       color: "#8b5cf6",
//       light: "rgba(139,92,246,0.1)",
//       tag: "Inspection",
//     },
//     {
//       id: "iso22000",
//       label: "ISO 22000:2018",
//       sublabel: "Food Safety Management",
//       file: "ISO 22000.2018.pdf",
//       icon: "bi-lightning",
//       color: "#10b981",
//       light: "rgba(16,185,129,0.1)",
//       tag: "ISO",
//     },
//     {
//       id: "iso9001",
//       label: "ISO 9001:2015",
//       sublabel: "Quality Management System",
//       file: "ISO9001.2015.pdf",
//       icon: "bi-check-circle-fill",
//       color: "#ec4899",
//       light: "rgba(236,72,153,0.1)",
//       tag: "ISO",
//     },
//   ];

//   const activeDocObj = activeDoc ? docs.find((d) => d.id === activeDoc) : null;

//   return (
//     <>
//       <section
//         id="cert-downloads"
//         ref={ref}
//         style={{
//           padding: "20px 24px min(80px,8vw)",
//           position: "relative",
//           overflow: "hidden",
//           marginTop: -150,
//         }}
//       >
//         {/* Decorative vertical line */}
//         <div
//           style={{
//             position: "absolute",
//             left: "50%",
//             top: 0,
//             bottom: 0,
//             width: 1,
//             background:
//               "linear-gradient(to bottom,transparent,rgba(221,60,48,0.08) 20%,rgba(221,60,48,0.08) 80%,transparent)",
//             pointerEvents: "none",
//           }}
//         />

//         <div
//           style={{
//             maxWidth: 1160,
//             margin: "0 auto",
//             position: "relative",
//             zIndex: 1,
//           }}
//         >
//           {/* 7-card grid — 4 + 3 */}
//           <div
//             style={{
//               display: "grid",
//               gridTemplateColumns: "repeat(4,1fr)",
//               gap: 10,
//               marginBottom: 10,
//             }}
//           >
//             {docs.slice(0, 4).map((doc, i) => (
//               <CertCard
//                 key={doc.id}
//                 doc={doc}
//                 index={i}
//                 vis={vis}
//                 isHov={hovIdx === doc.id}
//                 isActive={activeDoc === doc.id}
//                 onHover={setHovIdx}
//                 onOpen={setActiveDoc}
//               />
//             ))}
//           </div>
//           <div
//             style={{
//               display: "grid",
//               gridTemplateColumns: "repeat(4,1fr)",
//               gap: 10,
//             }}
//           >
//             {docs.slice(4).map((doc, i) => (
//               <CertCard
//                 key={doc.id}
//                 doc={doc}
//                 index={i + 4}
//                 vis={vis}
//                 isHov={hovIdx === doc.id}
//                 isActive={activeDoc === doc.id}
//                 onHover={setHovIdx}
//                 onOpen={setActiveDoc}
//               />
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* ── Full-screen PDF preview modal ── */}
//       {activeDoc && activeDocObj && (
//         <div
//           style={{
//             position: "fixed",
//             inset: 0,
//             zIndex: 9999,
//             display: "flex",
//             flexDirection: "column",
//             background: "rgba(4,8,5,0.97)",
//             backdropFilter: "blur(12px)",
//           }}
//         >
//           {/* Modal top bar */}
//           <div
//             style={{
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "space-between",
//               padding: "14px 28px",
//               borderBottom: "1px solid rgba(255,255,255,0.07)",
//               background: "#0a120b",
//               flexShrink: 0,
//             }}
//           >
//             <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
//               <span style={{ fontSize: 22 }}>
//                 <i className={`bi ${activeDocObj.icon}`}></i>
//               </span>
//               <div>
//                 <div
//                   className="pf"
//                   style={{
//                     fontSize: 16,
//                     fontWeight: 800,
//                     color: "#fff",
//                     lineHeight: 1.2,
//                   }}
//                 >
//                   {activeDocObj.label}
//                 </div>
//                 <div
//                   style={{
//                     fontSize: 11,
//                     color: "rgba(255,255,255,0.4)",
//                     fontFamily: "'DM Sans',sans-serif",
//                     letterSpacing: 0.5,
//                   }}
//                 >
//                   {activeDocObj.sublabel}
//                 </div>
//               </div>
//             </div>
//             <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
//               {/* Download button */}
//               <a
//                 href={`/assets/docs/${activeDocObj.file}`}
//                 download
//                 style={{
//                   display: "inline-flex",
//                   alignItems: "center",
//                   gap: 8,
//                   background: "#276530",
//                   color: "#fff",
//                   padding: "8px 22px",
//                   borderRadius: 9,
//                   fontSize: 13,
//                   fontWeight: 700,
//                   textDecoration: "none",
//                   fontFamily: "'DM Sans',sans-serif",
//                   boxShadow: "0 4px 16px rgba(39,101,48,0.4)",
//                   transition: "transform 0.2s,box-shadow 0.2s",
//                 }}
//                 onMouseEnter={(e) => {
//                   e.currentTarget.style.transform = "scale(1.04)";
//                   e.currentTarget.style.boxShadow =
//                     "0 8px 24px rgba(39,101,48,0.5)";
//                 }}
//                 onMouseLeave={(e) => {
//                   e.currentTarget.style.transform = "scale(1)";
//                   e.currentTarget.style.boxShadow =
//                     "0 4px 16px rgba(39,101,48,0.4)";
//                 }}
//               >
//                 <i className="bi bi-download"></i> Download PDF
//               </a>
//               {/* Close */}
//               <button
//                 onClick={() => setActiveDoc(null)}
//                 style={{
//                   background: "rgba(255,255,255,0.07)",
//                   border: "1px solid rgba(255,255,255,0.1)",
//                   borderRadius: 9,
//                   color: "#fff",
//                   width: 38,
//                   height: 38,
//                   display: "flex",
//                   alignItems: "center",
//                   justifyContent: "center",
//                   cursor: "pointer",
//                   fontSize: 18,
//                   transition: "background 0.2s",
//                 }}
//                 onMouseEnter={(e) => {
//                   e.currentTarget.style.background = "rgba(255,255,255,0.14)";
//                 }}
//                 onMouseLeave={(e) => {
//                   e.currentTarget.style.background = "rgba(255,255,255,0.07)";
//                 }}
//               >
//                 <i className="bi bi-x-lg"></i>
//               </button>
//             </div>
//           </div>

//           {/* PDF iframe */}
//           <iframe
//             key={activeDoc}
//             src={`/assets/docs/${activeDocObj.file}`}
//             title={activeDocObj.label}
//             style={{
//               flex: 1,
//               border: "none",
//               width: "100%",
//               background: "#1a1a1a",
//             }}
//           />
//         </div>
//       )}
//     </>
//   );
// }

/* Individual cert card sub-component */
function CertCard({ doc, index, vis, isHov, isActive, onHover, onOpen }) {
  const [cardRef, cardVis] = usePolicyCardVis(0.08);

  return (
    <div
      ref={cardRef}
      onMouseEnter={() => onHover(doc.id)}
      onMouseLeave={() => onHover(null)}
      onClick={() => onOpen(isActive ? null : doc.id)}
      style={{
        position: "relative",
        borderRadius: 28,
        overflow: "hidden",
        padding: 24,
        cursor: "pointer",
        background: "#ffffff",
        border: `1px solid ${
          isHov || isActive ? `${doc.color}35` : "rgba(15,23,42,0.08)"
        }`,
        boxShadow: isHov
          ? `
            0 20px 50px rgba(0,0,0,0.08),
            0 10px 30px ${doc.color}15
          `
          : `
            0 8px 30px rgba(0,0,0,0.05)
          `,
        transform: cardVis
          ? isHov
            ? "translateY(-10px)"
            : "translateY(0px)"
          : "translateY(40px) scale(0.95)",
        opacity: cardVis ? 1 : 0,
        transition: `
          all .55s cubic-bezier(.22,1,.36,1)
        `,
      }}
    >
      {/* Soft glow */}
      <div
        style={{
          position: "absolute",
          width: 180,
          height: 180,
          borderRadius: "50%",
          background: `${doc.color}12`,
          filter: "blur(60px)",
          top: -80,
          right: -60,
          opacity: isHov || isActive ? 1 : 0.5,
          transition: "all .4s ease",
          pointerEvents: "none",
        }}
      />

      {/* Top accent */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: 4,
          background: `linear-gradient(
            90deg,
            ${doc.color},
            transparent
          )`,
        }}
      />

      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 22,
          position: "relative",
          zIndex: 2,
        }}
      >
        {/* Icon */}
        <div
          style={{
            width: 58,
            height: 58,
            borderRadius: 18,
            background: `
              linear-gradient(
                145deg,
                ${doc.color}18,
                #ffffff
              )
            `,
            border: `1px solid ${doc.color}25`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 26,
            color: doc.color,
            boxShadow: `0 10px 25px ${doc.color}15`,
            transform: isHov ? "rotate(-6deg) scale(1.08)" : "none",
            transition: "all .35s ease",
          }}
        >
          <i className={`bi ${doc.icon}`}></i>
        </div>

        {/* Tag */}
        <div
          style={{
            padding: "6px 14px",
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 1.5,
            textTransform: "uppercase",
            color: doc.color,
            background: `${doc.color}12`,
            border: `1px solid ${doc.color}20`,
          }}
        >
          {doc.tag}
        </div>
      </div>

      {/* Content */}
      <div
        style={{
          position: "relative",
          zIndex: 2,
          marginBottom: 22,
        }}
      >
        <div
          style={{
            fontSize: 15,
            fontWeight: 800,
            color: "#111827",
            lineHeight: 1.25,
            letterSpacing: -0.5,
          }}
        >
          {doc.label}
        </div>

        <div
          style={{
            fontSize: 13,
            color: "#6b7280",
            lineHeight: 1.6,
            fontWeight: 500,
          }}
        >
          {doc.sublabel}
        </div>
      </div>

      {/* Bottom buttons */}
      <div
        style={{
          display: "flex",
          gap: 10,
          position: "relative",
          zIndex: 2,
        }}
      >
        {/* Preview */}
        <div
          style={{
            flex: 1,
            height: 44,
            borderRadius: 14,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            fontSize: 13,
            fontWeight: 700,
            color: isActive ? "#fff" : doc.color,
            background: isActive
              ? `linear-gradient(
                  135deg,
                  ${doc.color},
                  ${doc.color}cc
                )`
              : `${doc.color}10`,
            border: `1px solid ${
              isActive ? `${doc.color}80` : `${doc.color}20`
            }`,
            transition: "all .3s ease",
            boxShadow: isActive ? `0 10px 25px ${doc.color}25` : "none",
          }}
        >
          <i
            className={`bi ${isActive ? "bi-check-lg" : "bi-eye"}`}
            style={{ fontSize: 14 }}
          ></i>
          {isActive ? "Viewing" : "Preview"}
        </div>
      </div>
    </div>
  );
}

/* ─── Why Partner ─── */
function WhyPartnerSection() {
  const wrapRef = useRef(null);
  const prog = useStickyProgress(wrapRef);
  const reasons = [
    {
      tag: "Premium Quality",
      title: "Unmatched\nQuality Standards.",
      body: "Every item we supply goes through rigorous quality checks — at source, in our warehouse, and before dispatch. Our Grade A+ produce consistently exceeds the expectations of the UAE's most discerning chefs and procurement managers.",
      stat: "A+",
      statLbl: "Quality Grade",
      img: IMG.visionMission,
      col: "#163d1f",
      icon: "bi-award",
    },
  ];
  const ci = Math.min(reasons.length - 1, Math.floor(prog * reasons.length));
  const card = reasons[ci];
  return (
    <section
      id="why"
      ref={wrapRef}
      style={{
        height: `${100 * (reasons.length + 1.5)}vh`,
        position: "relative",
      }}
    >
      <div
        style={{
          position: "sticky",
          top: 104,
          height: "calc(100vh - 104px)",
          overflow: "hidden",
          background: "#090909",
        }}
      >
        {reasons.map((r, i) => (
          <img
            key={i}
            src={r.img}
            alt=""
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              filter: "brightness(0.6) saturate(1.1)",
              opacity: i === ci ? 1 : 0,
              transition: "opacity 0.75s ease",
            }}
          />
        ))}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(135deg,${card.col}bb 0%,transparent 65%)`,
            transition: "background 0.7s ease",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to left,rgba(0,0,0,0.7) 0%,transparent 60%)",
          }}
        />
        <div
          style={{
            position: "relative",
            zIndex: 2,
            maxWidth: 1312,
            margin: "0 auto",
            padding: "0 min(64px,6.4vw)",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ maxWidth: 520, color: "#fff" }}>
            <div
              className="sl"
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              <span style={{ fontSize: 18 }}>
                <i className={`bi ${card.icon}`}></i>
              </span>
              {card.tag}
            </div>
            <h2
              className="pf"
              style={{
                fontSize: "clamp(36px,4.8vw,52px)",
                fontWeight: 800,
                lineHeight: 1.08,
                letterSpacing: -2,
                marginBottom: 22,
                whiteSpace: "pre-line",
              }}
            >
              {card.title}
            </h2>
            <p
              style={{
                fontSize: 17,
                lineHeight: 1.3,
                color: "rgba(255,255,255,0.72)",
                marginBottom: 44,
                fontFamily: "'DM Sans',sans-serif",
              }}
            >
              {card.body}
            </p>
            <div style={{ marginBottom: 48 }}>
              <div
                className="pf"
                style={{
                  fontSize: 64,
                  fontWeight: 900,
                  color: "#dd3c30",
                  lineHeight: 1,
                }}
              >
                {card.stat}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "rgba(255,255,255,0.45)",
                  marginTop: 6,
                  letterSpacing: 0.5,
                  fontFamily: "'DM Sans',sans-serif",
                }}
              >
                {card.statLbl}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {reasons.map((_, i) => (
                <div
                  key={i}
                  className={`progress-pill${i === ci ? " active" : ""}`}
                />
              ))}
            </div>
          </div>
        </div>
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            background: "rgba(255,255,255,0.1)",
          }}
        >
          <div
            style={{
              height: "100%",
              background: "#dd3c30",
              width: `${prog * 100}%`,
              transition: "width 0.1s linear",
            }}
          />
        </div>
      </div>
    </section>
  );
}

/* ─── Vision & Mission ─── */
function VisionMissionSection() {
  const isMobile = useIsMobile();
  const ref = useRef(null);
  const vis = useInView(ref, 0.1);
  return (
    <section
      id="vision"
      ref={ref}
      style={{ background: "#f7fdf4", padding: "min(100px,10vw) 24px" }}
    >
      <div style={{ maxWidth: 1312, margin: "0 auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: 48,
            alignItems: "center",
          }}
        >
          <div
            style={{
              opacity: vis ? 1 : 0,
              transform: vis ? "none" : "translateX(-40px)",
              transition: "all 0.9s cubic-bezier(.22,1,.36,1)",
            }}
          >
            <div className="sl-dark">Who We Are</div>
            <h2
              className="pf"
              style={{
                fontSize: "clamp(32px,4vw,50px)",
                fontWeight: 900,
                letterSpacing: -2,
                color: "#0d0d0d",
                lineHeight: 1.08,
                marginBottom: 20,
              }}
            >
              Delivering Freshness.
              <br />
              <span style={{ color: "#276530" }}>Delivering Trust.</span>
            </h2>
            <p
              style={{
                fontSize: 16,
                color: "#444",
                lineHeight: 1.3,
                marginBottom: 24,
                fontFamily: "'DM Sans',sans-serif",
              }}
            >
              Fruits Garden Trading LLC is a leading fresh fruits and vegetables
              supply company based in Dubai, UAE. Since our founding in 2002, we
              have been the preferred HORECA partner for hundreds of hotels,
              restaurants, catering companies, and food service businesses
              across the UAE.
            </p>
            <p
              style={{
                fontSize: 16,
                color: "#444",
                lineHeight: 1.3,
                fontFamily: "'DM Sans',sans-serif",
              }}
            >
              Our strength lies in our ability to combine the reliability of a
              local partner with the reach of an international sourcing network
              — delivering quality produce, every day, without compromise.
            </p>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 16,
              opacity: vis ? 1 : 0,
              transform: vis ? "none" : "translateX(40px)",
              transition: "all 0.9s cubic-bezier(.22,1,.36,1) 0.15s",
            }}
          >
            {[
              {
                tag: (
                  <>
                    <i className="bi bi-bullseye"></i> Our Vision
                  </>
                ),
                title:
                  "To be the UAE's most trusted and innovative fresh produce partner — setting the standard for quality, reliability, and sustainability in the HORECA sector.",
              },
              {
                tag: (
                  <>
                    <i className="bi bi-rocket-takeoff"></i> Our Mission
                  </>
                ),
                title:
                  "To source the finest fresh fruits and vegetables from across the globe and deliver them to our clients with uncompromising freshness, consistency, and care — every single day.",
              },
            ].map((card, i) => (
              <div
                key={i}
                style={{
                  background: "#fff",
                  borderRadius: 20,
                  padding: "32px",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
                  border: "1px solid rgba(39,101,48,0.1)",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    letterSpacing: 2.5,
                    fontWeight: 800,
                    color: "#276530",
                    textTransform: "uppercase",
                    fontFamily: "'DM Sans',sans-serif",
                    marginBottom: 12,
                  }}
                >
                  {card.tag}
                </div>
                <p
                  className="pf"
                  style={{
                    fontSize: 17,
                    color: "#1a1a1a",
                    lineHeight: 1.3,
                    fontWeight: 700,
                  }}
                >
                  {card.title}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Food Safety Culture ─── */
function FoodSafetyCultureSection() {
  const wrapRef = useRef(null);
  const prog = useStickyProgress(wrapRef);
  const ref = useRef(null);
  const vis = useInView(ref, 0.05);

  const points = [
    {
      icon: "bi-buildings",
      color: "#276530",
      title: "HACCP Implementation Support",
      body: "A strong Food Safety Culture that supports the effective implementation of HACCP by ensuring that all employees understand and adhere to food safety protocols.",
    },
    {
      icon: "bi-gem",
      color: "#0ea5e9",
      title: "Strong Leadership",
      body: "Strong leadership that promotes a culture of safety and encourages staff to prioritize food safety in daily tasks across all levels of the organization.",
    },
    {
      icon: "bi-shield-check",
      color: "#f59e0b",
      title: "Reducing Foodborne Risks",
      body: "The Food Safety Culture has a strong influence on how employees respond to HACCP guidelines, making it crucial for reducing risks associated with foodborne illness.",
    },
    {
      icon: "bi-clipboard-check",
      color: "#8b5cf6",
      title: "Communicated Responsibilities",
      body: "Fruits Garden Trading LLC has communicated the responsibilities and authorities to enhance the positive food safety culture throughout the organization.",
    },
    {
      icon: "bi-wrench-adjustable",
      color: "#ec4899",
      title: "Adequate Resources",
      body: "Fruits Garden Trading LLC will provide adequate resources to ensure food is handled and delivered safely at every stage of the supply chain.",
    },
    {
      icon: "bi-mortarboard",
      color: "#10b981",
      title: "Staff Training & Awareness",
      body: "Fruits Garden Trading LLC will train their staff to understand the nature of hazards associated with food, sorting and storage conditions as well as equipment.",
    },
    {
      icon: "bi-file-text",
      color: "#f97316",
      title: "Legal Compliance & Continuous Improvement",
      body: "Fruits Garden Trading LLC staff are trained and are aware of legal requirements and continuously improve their practices to maintain the highest standards.",
    },
  ];

  const ci = Math.min(points.length - 1, Math.floor(prog * points.length));

  return (
    <section
      ref={wrapRef}
      id="food-safety-culture"
      style={{
        height: `${100 * (points.length + 1.5)}vh`,
        position: "relative",
      }}
    >
      <div
        style={{
          position: "sticky",
          top: 104,
          height: "calc(100vh - 104px)",
          overflow: "hidden",
          background: "#080808",
          display: "flex",
          alignItems: "center",
        }}
      >
        {/* Ghost text bg */}
        <div
          style={{
            position: "absolute",
            right: "-2%",
            bottom: "5%",
            pointerEvents: "none",
            zIndex: 0,
          }}
        >
          <span
            className="pf"
            style={{
              fontSize: "clamp(100px,18vw,280px)",
              fontWeight: 900,
              color: "rgba(255,255,255,0.025)",
              lineHeight: 1,
              letterSpacing: -6,
              whiteSpace: "nowrap",
            }}
          >
            Safety
          </span>
        </div>

        {/* Animated radial bg tied to active point */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(ellipse at 70% 50%, ${points[ci].color}18 0%, transparent 55%)`,
            transition: "background 0.6s ease",
            pointerEvents: "none",
          }}
        />

        <div
          ref={ref}
          style={{
            position: "relative",
            zIndex: 2,
            maxWidth: 1312,
            margin: "0 auto",
            padding: "0 min(64px,6.4vw)",
            width: "100%",
            display: "flex",
            gap: 64,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {/* Left — heading + active detail */}
          <div style={{ flex: 1, minWidth: 280, color: "#fff" }}>
            <div
              className="sl"
              style={{
                opacity: vis ? 1 : 0,
                transform: vis ? "none" : "translateY(20px)",
                transition: "all 0.8s ease",
              }}
            >
              About Us · Food Safety Culture
            </div>
            <h2
              className="pf"
              style={{
                fontSize: "clamp(32px,4vw,32px)",
                fontWeight: 900,
                letterSpacing: -2,
                lineHeight: 1.08,
                marginBottom: 28,
                opacity: vis ? 1 : 0,
                transform: vis ? "none" : "translateY(30px)",
                transition: "all 0.9s ease 0.1s",
              }}
            >
              Positive Food{" "}
              <span style={{ color: "#dd3c30" }}>Safety Culture</span>
            </h2>
            <p
              style={{
                fontSize: 15,
                color: "rgba(255,255,255,0.55)",
                lineHeight: 1.45,
                maxWidth: 460,
                marginBottom: 36,
                fontFamily: "'DM Sans',sans-serif",
                opacity: vis ? 1 : 0,
                transition: "all 0.9s ease 0.2s",
              }}
            >
              Fruits Garden Trading LLC establishes a strong positive food
              safety culture as fundamental to the successful functioning of any
              food hygiene system — acknowledging the importance of human
              behaviour in providing safe and suitable food.
            </p>

            {/* Active point detail card */}
            <div
              style={{
                background: `linear-gradient(135deg, ${points[ci].color}20, ${points[ci].color}08)`,
                border: `1px solid ${points[ci].color}40`,
                borderRadius: 20,
                padding: "28px 32px",
                transition: "all 0.5s ease",
                boxShadow: `0 20px 60px ${points[ci].color}15`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  marginBottom: 14,
                }}
              >
                <i className={points[ci].icon} style={{ fontSize: 32 }}></i>
                <div
                  style={{
                    fontSize: 11,
                    letterSpacing: 2.5,
                    fontWeight: 800,
                    color: points[ci].color,
                    textTransform: "uppercase",
                    fontFamily: "'DM Sans',sans-serif",
                  }}
                >
                  Point {ci + 1} of {points.length}
                </div>
              </div>
              <h3
                className="pf"
                style={{
                  fontSize: 22,
                  fontWeight: 800,
                  color: "#fff",
                  marginBottom: 12,
                  lineHeight: 1.2,
                }}
              >
                {points[ci].title}
              </h3>
              <p
                style={{
                  fontSize: 14.5,
                  color: "rgba(255,255,255,0.68)",
                  lineHeight: 1.45,
                  fontFamily: "'DM Sans',sans-serif",
                }}
              >
                {points[ci].body}
              </p>
            </div>

            {/* Pill indicators */}
            <div style={{ display: "flex", gap: 7, marginTop: 28 }}>
              {points.map((p, i) => (
                <div
                  key={i}
                  style={{
                    height: 5,
                    width: i === ci ? 28 : 5,
                    borderRadius: 3,
                    background:
                      i === ci ? points[ci].color : "rgba(255,255,255,0.15)",
                    transition: "all 0.4s ease",
                  }}
                />
              ))}
            </div>
          </div>

          {/* Right — stacked list of all points */}
          <div
            style={{
              flex: 0.85,
              minWidth: 280,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {points.map((p, i) => {
              const isActive = i === ci;
              const itemProg = clamp(prog * points.length - i + 0.4, 0, 1);
              return (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    padding: "14px 18px",
                    borderRadius: 14,
                    background: isActive
                      ? `${p.color}18`
                      : "rgba(255,255,255,0.025)",
                    border: `1px solid ${isActive ? p.color + "40" : "rgba(255,255,255,0.06)"}`,
                    transform: `translateX(${clamp((1 - itemProg) * -50, -50, 0)}px)`,
                    opacity: itemProg,
                    transition: "background 0.4s ease, border 0.4s ease",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {isActive && (
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 3,
                        background: p.color,
                        borderRadius: "3px 0 0 3px",
                      }}
                    />
                  )}
                  <span
                    style={{
                      fontSize: isActive ? 22 : 17,
                      transition: "font-size 0.4s",
                    }}
                  >
                    <i className={`bi ${p.icon}`} style={{ color: "#fff" }}></i>
                  </span>
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: isActive ? 700 : 400,
                      color: isActive ? "#fff" : "rgba(255,255,255,0.45)",
                      fontFamily: isActive
                        ? "'Playfair Display',serif"
                        : "'DM Sans',sans-serif",
                      transition: "all 0.4s",
                      flex: 1,
                    }}
                  >
                    {p.title}
                  </span>
                  {isActive && (
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: p.color,
                        flexShrink: 0,
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Progress bar */}
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            background: "rgba(255,255,255,0.05)",
          }}
        >
          <div
            style={{
              height: "100%",
              background: `linear-gradient(90deg,#276530,${points[ci].color})`,
              width: `${prog * 100}%`,
              transition: "width 0.08s linear",
            }}
          />
        </div>
      </div>
    </section>
  );
}

/* ─── Food Safety Policy ─── */
/* Individual card observer — fires once per card as it enters viewport */
function usePolicyCardVis(threshold = 0.15) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const o = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVis(true);
          o.disconnect();
        }
      },
      { threshold },
    );
    o.observe(ref.current);
    return () => o.disconnect();
  }, []);
  return [ref, vis];
}

/* Wrapper component so each card owns its own ref + vis state */
function PolicyCard({ point, index, hovState, setHovState }) {
  const [cardRef, cardVis] = usePolicyCardVis(0.1);
  const isHov = hovState === index;

  /* Direction alternates by column position (index % 3):
     col 0 → slides from left, col 1 → rises from below, col 2 → slides from right */
  const col = index % 3;
  const hiddenTransform =
    col === 0
      ? "translateX(-48px) translateY(24px) scale(0.94)"
      : col === 2
        ? "translateX(48px) translateY(24px) scale(0.94)"
        : "translateY(56px) scale(0.93)";

  /* Stagger within each row of 3 — col index gives the delay offset */
  const delay = col * 0.12;

  return (
    <div
      ref={cardRef}
      style={{
        background: isHov
          ? "linear-gradient(135deg,rgba(39,101,48,0.04),#fff)"
          : "#fff",
        borderRadius: 18,
        padding: "28px 26px",
        border: `1px solid ${isHov ? "rgba(39,101,48,0.28)" : "rgba(39,101,48,0.1)"}`,
        boxShadow: isHov
          ? "0 16px 48px rgba(39,101,48,0.14)"
          : "0 2px 12px rgba(0,0,0,0.04)",
        opacity: cardVis ? 1 : 0,
        transform: cardVis ? "none" : hiddenTransform,
        /* Two-layer transition: card entrance uses cubic-bezier spring,
           hover uses a quicker ease */
        transition: cardVis
          ? `opacity 0.65s cubic-bezier(.22,1,.36,1) ${delay}s,
             transform 0.65s cubic-bezier(.22,1,.36,1) ${delay}s,
             box-shadow 0.3s ease,
             border-color 0.3s ease,
             background 0.3s ease`
          : `opacity 0.65s cubic-bezier(.22,1,.36,1) ${delay}s,
             transform 0.65s cubic-bezier(.22,1,.36,1) ${delay}s`,
        position: "relative",
        overflow: "hidden",
        cursor: "default",
      }}
      onMouseEnter={() => setHovState(index)}
      onMouseLeave={() => setHovState(null)}
    >
      {/* Left accent — grows on hover */}
      <div
        style={{
          position: "absolute",
          left: 0,
          top: isHov ? 0 : "20%",
          bottom: isHov ? 0 : "20%",
          width: isHov ? 4 : 3,
          background: "linear-gradient(to bottom,#276530,#dd3c30)",
          borderRadius: "3px 0 0 3px",
          transition: "all 0.35s cubic-bezier(.22,1,.36,1)",
        }}
      />
      {/* Number badge + divider line */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 14,
        }}
      >
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: "50%",
            background: isHov ? "#276530" : "rgba(39,101,48,0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            fontWeight: 900,
            color: isHov ? "#fff" : "#276530",
            fontFamily: "'DM Sans',sans-serif",
            flexShrink: 0,
            transition: "all 0.3s ease",
          }}
        >
          {String(index + 1).padStart(2, "0")}
        </div>
        <div
          style={{
            flex: 1,
            height: 1,
            background: isHov ? "rgba(39,101,48,0.25)" : "rgba(39,101,48,0.12)",
            borderRadius: 1,
            transition: "background 0.3s ease",
          }}
        />
      </div>
      <p
        style={{
          fontSize: 13.5,
          color: isHov ? "#222" : "#444",
          lineHeight: 1.45,
          fontFamily: "'DM Sans',sans-serif",
          transition: "color 0.3s ease",
        }}
      >
        {point}
      </p>
    </div>
  );
}

function FoodSafetyPolicySection() {
  const isMobile = useIsMobile();
  const ref = useRef(null);
  const vis = useInView(ref, 0.05);
  const [hovState, setHovState] = useState(null);

  const policyPoints = [
    "Fruits Garden Trading provides food-safe and high-quality products to customers according to their intended use.",
    "Follow the Statutory, Regulatory, Codex Alimentarius Guidelines (HACCP), ISO 22000:2018 (FSMS) Food Safety Management System and ISO 9001:2015 (QMS) Quality Management standard requirements in our operations.",
    "Ensure that suppliers and contractors embrace the same food safety commitments and monitor the materials and services they supply.",
    "Provide adequate resources for the implementation of the HACCP, ISO 22000:2018 (FSMS) & ISO 9001:2015 (QMS) Standards.",
    "Ensure all food handlers are trained in food hygiene and competent in their work activities.",
    "Create a food safety culture within the organization and make every concerned employee accountable for food safety management efforts towards the elimination of hazards.",
    "Continually improve and update the Food Safety Management System and food safety culture requirements, adopt new ideas and technologies to improve the product quality & safety.",
    "Communicate, implement, and maintain Food Safety Policy at all levels of the organization and set annual measurable Food Safety Objectives to ensure continuous improvement.",
    "Developed a communication system to address all levels within the company and all interested parties as well and reviewed when necessary.",
  ];

  return (
    <section
      id="food-safety-policy"
      ref={ref}
      style={{
        background: "#f5f9f3",
        padding: "min(100px,10vw) 24px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Subtle grid bg */}
      <svg
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          opacity: 0.04,
          pointerEvents: "none",
        }}
      >
        {Array.from({ length: 16 }).map((_, i) => (
          <line
            key={`h${i}`}
            x1="0"
            y1={`${i * 6.66}%`}
            x2="100%"
            y2={`${i * 6.66}%`}
            stroke="#276530"
            strokeWidth="1"
          />
        ))}
        {Array.from({ length: 20 }).map((_, i) => (
          <line
            key={`v${i}`}
            x1={`${i * 5.26}%`}
            y1="0"
            x2={`${i * 5.26}%`}
            y2="100%"
            stroke="#276530"
            strokeWidth="1"
          />
        ))}
      </svg>

      <div
        style={{
          maxWidth: 1160,
          margin: "0 auto",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: 80,
            alignItems: "flex-start",
            marginBottom: 72,
            opacity: vis ? 1 : 0,
            transform: vis ? "none" : "translateY(36px)",
            transition: "all 0.9s cubic-bezier(.22,1,.36,1)",
          }}
        >
          <div>
            <h2
              className="pf"
              style={{
                fontSize: "clamp(30px,3.8vw,52px)",
                fontWeight: 900,
                letterSpacing: -2,
                color: "#0d0d0d",
                lineHeight: 1.08,
                marginBottom: 20,
              }}
            >
              Food Safety <span style={{ color: "#276530" }}>Policy</span>
            </h2>
            <p
              style={{
                fontSize: 15,
                color: "#555",
                lineHeight: 1.45,
                fontFamily: "'DM Sans',sans-serif",
                marginBottom: 28,
              }}
            >
              Fruits Garden Trading LLC is committed to the highest standards of
              food safety across all operations. Our policy governs every
              process, supplier relationship, and employee interaction within
              our supply chain.
            </p>
            {/* Certification badges */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              {[
                ["bi-shield-check", "HACCP 2020"],
                ["bi-check-circle-fill", "ISO 22000:2018"],
                ["bi-clipboard-check", "ISO 9001:2015"],
                ["bi-star-fill", "ADNOC Vendor"],
              ].map(([ic, lbl]) => (
                <div
                  key={lbl}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    background: "#fff",
                    border: "1px solid rgba(39,101,48,0.15)",
                    borderRadius: 8,
                    padding: "6px 12px",
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#276530",
                    boxShadow: "0 2px 8px rgba(39,101,48,0.06)",
                  }}
                >
                  <i className={`bi ${ic}`} style={{ fontSize: 14 }}></i>
                  {lbl}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Policy Points — each card has its own scroll observer */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)",
            gap: 10,
          }}
        >
          {policyPoints.map((point, i) => (
            <PolicyCard
              key={i}
              point={point}
              index={i}
              hovState={hovState}
              setHovState={setHovState}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── CTA ─── */
function CTASection() {
  const ref = useRef(null);
  const vis = useInView(ref, 0.1);
  return (
    <section
      id="cta"
      ref={ref}
      style={{
        background: "#0d1a10",
        padding: "min(100px,10vw) 24px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          width: 500,
          height: 500,
          borderRadius: "50%",
          background:
            "radial-gradient(circle,rgba(39,101,48,0.18),transparent)",
          top: "50%",
          left: "8%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
          animation: "orbPulse 4s ease-in-out infinite",
        }}
      />
      <div
        style={{
          position: "absolute",
          width: 350,
          height: 350,
          borderRadius: "50%",
          background: "radial-gradient(circle,rgba(221,60,48,0.1),transparent)",
          top: "50%",
          right: "8%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
          animation: "orbPulse 4s ease-in-out infinite 2s",
        }}
      />
      <div
        style={{
          maxWidth: 1000,
          margin: "0 auto",
          textAlign: "center",
          position: "relative",
          zIndex: 1,
          opacity: vis ? 1 : 0,
          transform: vis ? "translateY(0)" : "translateY(40px)",
          transition: "all 0.9s ease",
        }}
      >
        <div
          className="sl"
          style={{
            justifyContent: "center",
            display: "flex",
            marginBottom: 20,
          }}
        >
          Ready to Partner?
        </div>
        <h2
          className="pf"
          style={{
            fontSize: "clamp(36px,5vw,56px)",
            fontWeight: 800,
            letterSpacing: -2,
            color: "#fff",
            lineHeight: 1.05,
            marginBottom: 20,
          }}
        >
          Let's Build Something
          <br />
          <span style={{ color: "#dd3c30" }}>Great Together.</span>
        </h2>
        <p
          style={{
            fontSize: 17,
            color: "rgba(255,255,255,0.6)",
            maxWidth: 600,
            margin: "0 auto min(52px,5.2vw)",
            lineHeight: 1.3,
            fontFamily: "'DM Sans',sans-serif",
          }}
        >
          Whether you're a hotel, restaurant, catering company, or retailer — we
          have the quality, the supply chain, and the expertise to meet your
          fresh produce needs every day.
        </p>
        <div
          style={{
            display: "flex",
            gap: 16,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <Link
            to="/contact-us"
            className="btn-green"
            style={{ fontSize: 16, padding: "16px min(52px,5.2vw)" }}
          >
            Get a Quote
          </Link>
          <a
            href="tel:+971565378650"
            className="btn-ghost"
            style={{ fontSize: 16, padding: "16px min(52px,5.2vw)" }}
          >
            <i className="bi bi-telephone"></i> Call Us Now
          </a>
        </div>
        <p
          style={{
            fontSize: 13,
            color: "rgba(255,255,255,0.3)",
            marginTop: 28,
            fontFamily: "'DM Sans',sans-serif",
          }}
        >
          +971 56 537 8650 · sales@fruitsgarden.com · www.fruitsgarden.com
        </p>
      </div>
    </section>
  );
}

function BackTop({ scrollY }) {
  if (scrollY < 600) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      style={{
        position: "fixed",
        bottom: 32,
        right: 32,
        zIndex: 999,
        width: 48,
        height: 48,
        borderRadius: "50%",
        background: "#dd3c30",
        color: "#fff",
        border: "none",
        cursor: "pointer",
        fontSize: 18,
        boxShadow: "0 4px 18px rgba(39,101,48,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "transform 0.2s,box-shadow 0.2s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "scale(1.1)";
        e.currentTarget.style.boxShadow = "0 8px 30px rgba(39,101,48,0.6)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1)";
        e.currentTarget.style.boxShadow = "0 4px 18px rgba(39,101,48,0.45)";
      }}
    >
      ↑
    </button>
  );
}

/* ─── PAGE ─── */
export default function AboutUs() {
  const scrollY = useScrollY();
  useEffect(() => {
    document.title = "About Us — Fruits Garden Trading LLC";
    const style = document.createElement("style");
    style.textContent = GLOBAL_CSS;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  return (
    <div>
      <Navbar scrollY={scrollY} />
      <HeroSection scrollY={scrollY} />
      <SecondaryNav scrollY={scrollY} />
      <StatsBanner />
      <StorySection />
      <VisionMissionSection />
      <CultureSection />
      <CoreValuesSection />
      <LeadershipSection />
      <MilestonesSection />
      <CertificationsSection />
      {/* <CertificateDownloadsSection /> */}
      <WhyPartnerSection />
      <FoodSafetyCultureSection />
      <FoodSafetyPolicySection />
      <CTASection />
      <Footer />
      <BackTop scrollY={scrollY} />
    </div>
  );
}
